/**
 * FI Task Queue API Service (Sprint 171 D-002)
 *
 * Type-safe client for the institutional FI task queue endpoints. Tasks are
 * derived from pledges awaiting FI officer or FI ops action; the backend lives
 * in platform-admin-api at /api/admin/tasks.
 *
 * Types derived from platform-admin.openapi.yaml -- no manual interfaces.
 *
 * Note on baseUrl: the canonical spec authors Task paths as `/admin/tasks/*`
 * (unlike other platform-admin operations which are mounted relative to
 * /api/admin). A dedicated client anchored at `/api` keeps the generated
 * path keys intact while producing the correct runtime URL
 * (/api + /admin/tasks = /api/admin/tasks).
 */

import createClient from 'openapi-fetch';
import type { paths as PlatformAdminPaths } from '@aaimplatform/institutional-types/platform-admin';
import type { components } from '@aaimplatform/institutional-types/platform-admin';
import { authMiddleware } from '../client';

// ---------------------------------------------------------------------------
// Type aliases from generated OpenAPI schema
// ---------------------------------------------------------------------------

export type Task = components['schemas']['Task'];
export type TaskAgeBand = components['schemas']['TaskAgeBand'];
export type TaskListResponse = components['schemas']['TaskListResponse'];
export type TaskCountResponse = components['schemas']['TaskCountResponse'];
export type BulkTaskActionRequest = components['schemas']['BulkTaskActionRequest'];
export type BulkTaskReturnRequest = components['schemas']['BulkTaskReturnRequest'];
export type BulkTaskActionResponse = components['schemas']['BulkTaskActionResponse'];
export type BulkTaskActionFailure = components['schemas']['BulkTaskActionFailure'];
export type ReassignTaskRequest = components['schemas']['ReassignTaskRequest'];
export type PledgeStatus = components['schemas']['PledgeStatus'];

// ---------------------------------------------------------------------------
// Dedicated openapi-fetch client for Task routes
// ---------------------------------------------------------------------------

const tasksClient = createClient<PlatformAdminPaths>({ baseUrl: '/api', credentials: 'include' });
tasksClient.use(authMiddleware);

// ---------------------------------------------------------------------------
// Query parameters
// ---------------------------------------------------------------------------

export interface TaskListQueryOptions {
  status?: PledgeStatus;
  ageBand?: TaskAgeBand;
  assignedReviewer?: string;
  packageId?: string;
  cursor?: string;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || result.data === undefined) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const body = result.error as { message?: string } | undefined;
    throw new Error(body?.message ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const tasksApi = {
  list: async (params?: TaskListQueryOptions): Promise<TaskListResponse> => {
    const result = await tasksClient.GET('/admin/tasks', {
      params: {
        query: {
          status: params?.status,
          ageBand: params?.ageBand,
          assignedReviewer: params?.assignedReviewer,
          packageId: params?.packageId,
          cursor: params?.cursor,
          limit: params?.limit,
        },
      },
    });
    return unwrap(result);
  },

  count: async (): Promise<TaskCountResponse> => {
    const result = await tasksClient.GET('/admin/tasks/count');
    return unwrap(result);
  },

  bulkAcknowledge: async (
    body: BulkTaskActionRequest,
  ): Promise<BulkTaskActionResponse> => {
    const result = await tasksClient.POST('/admin/tasks/bulk-acknowledge', {
      body,
    });
    return unwrap(result);
  },

  bulkReturn: async (
    body: BulkTaskReturnRequest,
  ): Promise<BulkTaskActionResponse> => {
    const result = await tasksClient.POST('/admin/tasks/bulk-return', {
      body,
    });
    return unwrap(result);
  },

  reassign: async (
    pledgeId: string,
    body: ReassignTaskRequest,
  ): Promise<Task> => {
    const result = await tasksClient.POST('/admin/tasks/{pledgeId}/reassign', {
      params: { path: { pledgeId } },
      body,
    });
    return unwrap(result);
  },
};
